import { useMemo } from 'react';
import { useAuthStore } from '@clarix/auth-store';
import { ROOT_DOMAIN, IS_SINGLE_HOST } from '@/config/env';

/**
 * Production layout: {slug}.{root} / {root}   (root = `VITE_ROOT_DOMAIN`)
 * Dev layout:        {slug}.localhost:5000 / localhost:5000
 *
 * In dev, modern browsers resolve `*.localhost` to 127.0.0.1 automatically (RFC 2606),
 * so you can hit `acme.localhost:5000` with no /etc/hosts edits.
 *
 * Returns `null` when the host IS the apex (no subdomain). The shell decides what to
 * do with null — typically redirect to a workspace picker or login screen.
 *
 * The slug is derived by STRIPPING the configured root domain, not by counting labels.
 * Label-counting only works for a 2-label root like `clarix.app`; a 3-label root such
 * as `clarix.sarvadhi.work` has 3 labels at its own apex and would be misread as a
 * workspace called `clarix`. Stripping the root is correct for any label count.
 * When no root is configured (e.g. local `*.localhost` dev) we fall back to the
 * first-label-is-slug convention.
 */

function slugFromRoot(hostname: string, root: string): string | null {
  // The apex itself has no workspace.
  if (hostname === root) return null;
  // `{slug}.{root}` → strip the `.{root}` suffix, the leftmost label is the slug.
  const suffix = `.${root}`;
  if (hostname.endsWith(suffix)) {
    const prefix = hostname.slice(0, -suffix.length);
    return prefix ? (prefix.split('.')[0] ?? null) : null;
  }
  // Host is unrelated to the configured root → treat as apex.
  return null;
}

export function getWorkspaceSlug(
  hostname: string = window.location.hostname,
  rootDomain: string | undefined = ROOT_DOMAIN,
): string | null {
  // Single-host deploys serve every workspace from one origin — there is no
  // subdomain slug, so the host is always the apex. The active workspace comes
  // from the session/JWT, not the URL.
  if (IS_SINGLE_HOST) return null;

  // Bare localhost or IP → apex domain in dev → no workspace selected yet.
  // The shell uses null to drive login/onboarding gates.
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  const parts = hostname.split('.');

  // *.localhost is the dev subdomain pattern — any 2+ part host ending in
  // `localhost` treats the first label as the slug (e.g. `acme.localhost` → 'acme').
  if (parts[parts.length - 1] === 'localhost') {
    return parts.length >= 2 ? (parts[0] ?? null) : null;
  }

  // Production: strip the configured root domain.
  if (rootDomain) {
    return slugFromRoot(hostname, rootDomain);
  }

  // No root configured → legacy heuristic: 3+ labels = subdomain, apex (2) → null.
  if (parts.length < 3) return null;
  return parts[0] ?? null;
}

/**
 * Reactive "which workspace slug is active right now" — the one thing every
 * component gating on workspace identity should read instead of calling
 * `getWorkspaceSlug()` directly. In single-host mode the URL carries no
 * subdomain, so `getWorkspaceSlug()` always returns null there; the real
 * answer lives in the auth session instead. AppLauncher, AppShellLayout,
 * AppSwitcher and ProfilePage each independently forgot this branch (each
 * one silently querying `/me` with no active slug — i.e. whatever workspace
 * `/me` defaults to — under single-host), which is exactly the class of bug
 * a single shared definition prevents from recurring at a sixth call site.
 */
export function useActiveWorkspaceSlug(): string | null {
  const sessionSlug = useAuthStore((s) => s.workspaceSlug);
  const parsedSlug = useMemo(() => getWorkspaceSlug(), []);
  return IS_SINGLE_HOST ? sessionSlug : parsedSlug;
}

/**
 * Resolve the apex/parent host (no slug) for the current location, honoring the
 * configured root domain. Shared by `buildWorkspaceUrl` / `buildApexUrl`.
 */
function parentHostFor(host: string, rootDomain: string | undefined): string {
  if (host === 'localhost' || host === '127.0.0.1') return 'localhost';

  const parts = host.split('.');
  if (parts[parts.length - 1] === 'localhost') {
    return parts.slice(1).join('.') || 'localhost';
  }

  // Root-domain-aware: the parent is always the configured root, regardless of
  // how many labels it has.
  if (rootDomain) return rootDomain;

  // No root configured → legacy: strip the first label of a 3+ label host.
  if (parts.length >= 3) return parts.slice(1).join('.');
  return host;
}

/**
 * Build the URL for a different workspace's subdomain on the same site.
 *
 *   dev:  http://acme.localhost:5000/
 *   prod: https://acme.clarix.sarvadhi.work/
 *
 * Preserves protocol + port + the current path so callers can deep-link by
 * passing `path` (e.g. /admin/apps) when they don't want to land on the home.
 */
export function buildWorkspaceUrl(
  slug: string,
  path: string = '/',
  loc: { protocol: string; hostname: string; port: string } = window.location,
  rootDomain: string | undefined = ROOT_DOMAIN,
): string {
  const port = loc.port ? `:${loc.port}` : '';
  const safePath = path.startsWith('/') ? path : `/${path}`;
  // Single-host: no per-workspace subdomain — stay on the current origin.
  if (IS_SINGLE_HOST) return `${loc.protocol}//${loc.hostname}${port}${safePath}`;
  const parentHost = parentHostFor(loc.hostname, rootDomain);
  return `${loc.protocol}//${slug}.${parentHost}${port}${safePath}`;
}

/** Apex (no-subdomain) URL, useful for redirecting to login/onboarding. */
export function buildApexUrl(
  path: string = '/',
  loc: { protocol: string; hostname: string; port: string } = window.location,
  rootDomain: string | undefined = ROOT_DOMAIN,
): string {
  const port = loc.port ? `:${loc.port}` : '';
  const safePath = path.startsWith('/') ? path : `/${path}`;
  // Single-host: the apex IS the current origin.
  if (IS_SINGLE_HOST) return `${loc.protocol}//${loc.hostname}${port}${safePath}`;
  const parentHost = parentHostFor(loc.hostname, rootDomain);
  return `${loc.protocol}//${parentHost}${port}${safePath}`;
}
